import { IconCheck, IconCopy } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import Tooltip from "@forento/ui/components/Tooltip"
import { cn } from "@forento/ui/utilities/tailwind"

import { useToast } from "~/contexts/ToastContext"
import { usePlatform } from "~/contexts/UserContext"

type Props = { id: number; className?: string }

/** Shows the ID that Zapier actions expect, with a copy button. Hidden on plans without Zapier. */
const ZapierId: FC<Props> = ({ id, className }) => {
	const platform = usePlatform()
	const toast = useToast()
	const [isCopied, setCopied] = useState(false)

	useEffect(() => {
		if (!isCopied) return
		const timeout = setTimeout(() => setCopied(false), 2000)
		return () => clearTimeout(timeout)
	}, [isCopied])

	if (!platform?.plan.access.zapier) return null

	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(id.toString())
			setCopied(true)
		} catch (error) {
			console.error(error)
			toast.showError("Couldn't copy the ID. Select it and copy it manually.")
		}
	}

	return (
		<div
			className={cn(
				"border-component-border flex items-center justify-between gap-2 rounded-lg border border-dashed bg-white/60 py-1 pr-1 pl-3 text-xs",
				className,
			)}
		>
			<span className="text-light">Zapier ID</span>
			<div className="flex items-center gap-1">
				<span className="font-mono">{id}</span>
				<Tooltip tooltip={isCopied ? "Copied" : "Copy ID"}>
					<button
						type="button"
						aria-label={isCopied ? "Copied Zapier ID" : "Copy Zapier ID"}
						onClick={handleCopy}
						className="hover:bg-component-background focus-visible:outline-primary cursor-pointer rounded-md p-1 text-gray-400 transition-colors hover:text-gray-700 focus-visible:outline-2"
					>
						{isCopied ? <IconCheck className="size-3.5" /> : <IconCopy className="size-3.5" />}
					</button>
				</Tooltip>
			</div>
		</div>
	)
}

export default ZapierId
